import { isAxiosError } from "axios";
import { z } from "zod";
import {
  getPayrollAgenticAPI,
  type MyPayEnvironment,
} from "../../generated/core-api";

export const ONBOARDING_COMPANY_LOOKUP_UNEXPECTED_SHAPE =
  "Company lookup returned an unexpected response shape.";

// Unknown / no-access (masked upstream) or malformed path param — not an outage.
// 401 is excluded: UnauthorizedInterceptor owns re-auth; callers must rethrow.
const INVALID_CODE_STATUSES = new Set([400, 404, 422]);

const companyLookupSchema = z.object({
  companyCode: z.string(),
  companyName: z.string(),
});

export type OnboardingCompanyLookupDto = z.infer<typeof companyLookupSchema>;

export class OnboardingCompanyNotFoundError extends Error {
  constructor() {
    super("Onboarding company not found");
    this.name = "OnboardingCompanyNotFoundError";
  }
}

/**
 * `GET /api/v1/onboarding/companies/{code}` — shared by company-code validation
 * and bulk-create Results read-back. Uses the Orval client (auth + 401 interceptor).
 */
export async function fetchOnboardingCompanyLookup(
  trimmedCode: string,
  mypayEnvironment: MyPayEnvironment
): Promise<OnboardingCompanyLookupDto> {
  try {
    const raw = await getPayrollAgenticAPI().getOnboardingCompany(
      encodeURIComponent(trimmedCode),
      { mypay_environment: mypayEnvironment }
    );
    const parsed = companyLookupSchema.safeParse(raw);
    if (!parsed.success) {
      throw new Error(ONBOARDING_COMPANY_LOOKUP_UNEXPECTED_SHAPE);
    }
    return parsed.data;
  } catch (error) {
    if (isAxiosError(error) && error.response != null) {
      const httpStatus = error.response.status;
      if (INVALID_CODE_STATUSES.has(httpStatus)) {
        throw new OnboardingCompanyNotFoundError();
      }
      if (httpStatus === 401) {
        throw error;
      }
    }
    throw error;
  }
}
